import { Editor, Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { getMarkRange } from '@tiptap/core';
import type { EditorView } from '@tiptap/pm/view';

class LinkMenuView {
  private editor: Editor;
  private view: EditorView;
  private menu: HTMLDivElement;
  private displayContainer: HTMLDivElement;
  private urlElement: HTMLDivElement;
  private editButton: HTMLButtonElement;
  private unlinkButton: HTMLButtonElement;
  private editContainer: HTMLDivElement;
  private editInput: HTMLInputElement;
  private saveButton: HTMLButtonElement;
  private cancelButton: HTMLButtonElement;

  constructor({ editor, view }: { editor: Editor; view: EditorView }) {
    this.editor = editor;
    this.view = view;

    this.menu = document.createElement('div');
    this.menu.className = 'link-menu';
    this.hide();

    this.displayContainer = document.createElement('div');
    this.displayContainer.className = 'flex gap-1 items-center overflow-hidden w-full';
    this.menu.appendChild(this.displayContainer);

    this.urlElement = document.createElement('div');
    this.urlElement.className = 'grow truncate p-1';
    this.displayContainer.appendChild(this.urlElement);

    this.editButton = document.createElement('button');
    this.editButton.type = 'button';
    this.editButton.className = 'link-menu-item';
    this.editButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M15.7279 9.57629L14.3137 8.16207L5 17.4758V18.89H6.41421L15.7279 9.57629ZM17.1421 8.16207L18.5563 6.74786L17.1421 5.33365L15.7279 6.74786L17.1421 8.16207ZM7.24264 20.89H3V16.6474L16.435 3.21233C16.8256 2.8218 17.4587 2.8218 17.8492 3.21233L20.6777 6.04075C21.0682 6.43128 21.0682 7.06444 20.6777 7.45497L7.24264 20.89Z"></path></svg>';
    this.displayContainer.appendChild(this.editButton);

    this.editButton.addEventListener('click', () => {
      this.edit();
    });

    this.unlinkButton = document.createElement('button');
    this.unlinkButton.type = 'button';
    this.unlinkButton.className = 'link-menu-item';
    this.unlinkButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M17 17H22V19H19V22H17V17ZM7 7H2V5H5V2H7V7ZM18.364 15.5355L16.9497 14.1213L18.364 12.7071C20.3166 10.7545 20.3166 7.58866 18.364 5.63604C16.4113 3.68342 13.2455 3.68342 11.2929 5.63604L9.87868 7.05025L8.46447 5.63604L9.87868 4.22183C12.6123 1.48816 17.0445 1.48816 19.7782 4.22183C22.5118 6.9555 22.5118 11.3877 19.7782 14.1213L18.364 15.5355ZM15.5355 18.364L14.1213 19.7782C11.3877 22.5118 6.9555 22.5118 4.22183 19.7782C1.48816 17.0445 1.48816 12.6123 4.22183 9.87868L5.63604 8.46447L7.05025 9.87868L5.63604 11.2929C3.68342 13.2455 3.68342 16.4113 5.63604 18.364C7.58866 20.3166 10.7545 20.3166 12.7071 18.364L14.1213 16.9497L15.5355 18.364ZM14.8284 7.75736L16.2426 9.17157L9.17157 16.2426L7.75736 14.8284L14.8284 7.75736Z"></path></svg>';
    this.displayContainer.appendChild(this.unlinkButton);
    this.unlinkButton.addEventListener('click', () => {
      this.unlink();
    });

    this.editContainer = document.createElement('div');
    this.editContainer.className = 'flex gap-1 items-center overflow-hidden w-full';
    this.editContainer.style.display = 'none';
    this.menu.appendChild(this.editContainer);

    this.editInput = document.createElement('input');
    this.editInput.type = 'text';
    this.editInput.className = 'grow form-control p-1';
    this.editInput.setAttribute('placeholder', 'https://');
    this.editContainer.appendChild(this.editInput);
    this.editInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.save();
      }
    });

    this.saveButton = document.createElement('button');
    this.saveButton.type = 'button';
    this.saveButton.className = 'link-menu-item';
    this.saveButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M10.0007 15.1709L19.1931 5.97852L20.6073 7.39273L10.0007 17.9993L3.63672 11.6354L5.05093 10.2212L10.0007 15.1709Z"></path></svg>';
    this.editContainer.appendChild(this.saveButton);
    this.saveButton.addEventListener('click', () => {
      this.save();
    });

    this.cancelButton = document.createElement('button');
    this.cancelButton.type = 'button';
    this.cancelButton.className = 'link-menu-item';
    this.cancelButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12.0007 10.5865L16.9504 5.63672L18.3646 7.05093L13.4149 12.0007L18.3646 16.9504L16.9504 18.3646L12.0007 13.4149L7.05093 18.3646L5.63672 16.9504L10.5865 12.0007L5.63672 7.05093L7.05093 5.63672L12.0007 10.5865Z"></path></svg>';
    this.editContainer.appendChild(this.cancelButton);
    this.cancelButton.addEventListener('click', () => {
      this.cancel();
    });

    view.dom.parentNode.appendChild(this.menu);

    this.update(view, null);
  }

  show() {
    this.menu.style.display = 'block';
  }

  hide() {
    this.menu.style.display = 'none';
  }

  edit() {
    this.displayContainer.style.display = 'none';
    this.editContainer.style.display = 'flex';
    this.editInput.value = this.editor.getAttributes('link').href;
    this.editInput.focus();
  }

  save() {
    if (this.editInput.value === '') {
      this.unlink();
    } else {
      const range = getMarkRange(this.view.state.selection.$to, this.editor.schema.marks.link);
      if (!range) return;
      const { from, to } = range;
      this.editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .setLink({ href: this.editInput.value })
        .run();
    }
  }

  cancel() {
    const href = this.editor.getAttributes('link').href;

    if (href === '') {
      this.unlink();
    } else {
      this.displayContainer.style.display = 'flex';
      this.editContainer.style.display = 'none';
    }
  }

  unlink() {
    const range = getMarkRange(this.view.state.selection.$to, this.editor.schema.marks.link);
    if (!range) return;
    const { from, to } = range;
    this.editor.chain().focus().setTextSelection({ from, to }).unsetLink().run();
  }

  update(view: EditorView, lastState: any) {
    let { state } = view;

    // Don't do anything if the document/selection didn't change
    if (lastState && lastState.doc.eq(state.doc) && lastState.selection.eq(state.selection)) {
      return;
    }

    if (!this.editor.isActive('link')) {
      this.hide();
      return;
    }

    // update content

    const href = this.editor.getAttributes('link').href;
    this.urlElement.innerHTML = `
      <a href=${href} target="_blank">${href}</a>
    `;

    this.show();

    // update position
    const { from, to } = state.selection;

    const start = view.coordsAtPos(from);
    const end = view.coordsAtPos(to);

    const box = this.menu.offsetParent.getBoundingClientRect();

    let left = (start.left + end.left) / 2;
    const minLeft = box.left + this.menu.offsetWidth / 2 + 24;
    const maxLeft = box.right - this.menu.offsetWidth / 2 - 24;

    if (left < minLeft) {
      left = minLeft;
    } else if (left > maxLeft) {
      left = maxLeft;
    }

    this.menu.style.left = `${left - box.left}px`;
    this.menu.style.top = `${end.bottom - box.top + 8}px`;

    // enter edit mode if href is empty
    if (href === '') {
      this.edit();
    } else {
      this.cancel();
    }
  }
}

const LinkMenu = Extension.create({
  name: 'linkMenu',

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        view(editorView) {
          return new LinkMenuView({ editor, view: editorView });
        },
      }),
    ];
  },
});

export default LinkMenu;
